import React, { useEffect, useState, useRef, Suspense } from 'react'
import {
    FiChevronRight,
    FiChevronDown,
    FiChevronUp,
    FiPlus
} from 'react-icons/fi';
import { Link, NavLink, Outlet, useParams, useNavigate } from 'react-router-dom';
import { AnalysisIcon, DiamondIcon, OverviewIcon, OverviewFilledIcon, ScoresIcon, SettingIcon, AnalysisFilledIcon, ScoresFilledIcon, DiamondFilledIcon, RadarIcon, RadarFilledIcon } from '../assets/icons'; //CompetitorIcon, CompetitorFilledIcon
import ConceptModal from '../components/modals/Result-Concept-Modal';
import MalePersonaImage from "../assets/png/male-persona.png";
import logo from "../assets/png/logoNew.svg";
// import classNames from "classnames";
import { httpRequest } from '../utils/http.util';
import { useError } from '../contexts/error.context';

// ============================================================
// FEATURE FLAGS INTEGRATION
// @author Sabya
// @date September 30, 2025
//
// Redux integration for dynamic feature flag management
// Fetches configuration once per project and caches it
// ============================================================
import { useDispatch, useSelector } from 'react-redux';
import { fetchFeatureConfig, resetFeatures, selectFeatures } from '../redux/slices/featureSlice';
import Product1 from '../assets/png/product-1.png';
import Product2 from '../assets/png/product-2.png';
import Product3 from '../assets/png/product-3.png';
import ResultPersonasModal from '../components/modals/Result-Personas-Modal';

const conceptImages = [Product1, Product2, Product3];

const ResultLayout = () => {
    const { projectId, runId } = useParams();
    const navigate = useNavigate();
    const [isAnalysisOpen, setIsAnalysisOpen] = useState(false);
    const [isConceptOpen, setIsConceptOpen] = useState(true);
    const [isPersonasOpen, setIsPersonasOpen] = useState(true);
    const [isCompetitorsOpen, setIsCompetitorsOpen] = useState(true);
    const [concepts, setConcepts] = useState(null);
    const [clickedStimuli, setClickedStimuli] = useState({
        visible: false,
        data: null,
        index: null
    });
    const [personas, setPersonas] = useState(null);
    const [clickedPersona, setClickedPersona] = useState({
        visible: false,
        persona: null,
    });
    const [competitors, setCompetitors] = useState(null);
    const [winningProduct, setWinningProduct] = useState(null);
    const [projectName, setProjectName] = useState('');
    const [projectType, setProjectType] = useState('');
    const { handleAPIError } = useError();

    // ============================================================
    // FEATURE FLAGS - Redux Integration
    // @author Sabya | @date September 30, 2025
    // ============================================================
    // Dispatch actions to Redux store
    const dispatch = useDispatch();

    // Select feature flags from Redux store
    // This automatically re-renders when features change
    const features = useSelector(selectFeatures);
    
    // Add refs to track if data has been loaded
    const dataLoaded = useRef({
        sidebar: false,
        summary: false
    });
    
    // Add refs to store the previous projectId and runId
    const prevProjectId = useRef(null);
    const prevRunId = useRef(null);

    const toggleAnalysis = () => {
        setIsAnalysisOpen(!isAnalysisOpen);
    };

    const handleProjectSetupClick = () => {
        // Always pass a state to indicate this is an existing project
        navigate(`/${projectId}/create-project`, { state: { isExistingProject: true } });
    };

    // ============================================================
    // FEATURE FLAGS - Fetch Configuration on Project Change
    // @author Sabya | @date September 30, 2025
    // ============================================================
    // Fetch feature config fresh when landing on results
    // Reset cache first to ensure success_factors are fetched from API
    // (they may not exist when config was first cached during project setup)
    useEffect(() => {
        if (projectId) {
            dispatch(resetFeatures());
            dispatch(fetchFeatureConfig(projectId));
        }
    }, [projectId, dispatch]);

    // Fetch sidebar data only once per project
    useEffect(() => {
        if (!projectId) return;

        // Check if we need to reload data (new project or not loaded yet)
        const needsReload = prevProjectId.current !== projectId || !dataLoaded.current.sidebar;

        if (needsReload) {
            const fetchData = async () => {
                try {
                    const [conceptsResponse, personasResponse, competitorsResponse] = await Promise.all([
                        httpRequest({
                            method: "GET",
                            url: `/concept_testing/${projectId}/concept_details`,
                            isAuthRequired: true
                        }),
                        httpRequest({
                            method: "GET",
                            url: `/concept_testing/${projectId}/personas/selected`,
                            isAuthRequired: true
                        }),
                        httpRequest({
                            method: "GET",
                            url: `/concept_testing/${projectId}/project_details`,
                            isAuthRequired: true
                        })
                    ]);

                    // Update all states at once
                    // Fixed: Concept image loading from API response - September 23, 2025 - Sabya
                    setConcepts(conceptsResponse.data);
                    setPersonas(personasResponse.data);
                    setCompetitors(competitorsResponse.data.competition_urls_detailed);
                    setProjectName(competitorsResponse.data.project_name || '');
                    setProjectType(competitorsResponse.data.project_type);
                    
                    // Mark data as loaded and update the previous projectId
                    dataLoaded.current.sidebar = true;
                    prevProjectId.current = projectId;
                } catch (error) {
                    handleAPIError(error);
                }
            };

            fetchData();
        }
    }, [projectId, handleAPIError]);

    // Fetch winning product only once per run
    useEffect(() => {
        if (!projectId || !runId) return;
        
        // Check if we need to reload data (new run or not loaded yet)
        const needsReload = prevRunId.current !== runId || prevProjectId.current !== projectId || !dataLoaded.current.summary;
        
        if (needsReload) {
            const fetchAPIData = async () => {
                try {
                    const response = await httpRequest({
                        method: "GET",
                        url: `/concept_testing/${projectId}/${runId}/summary`,
                        isAuthRequired: true,
                    });
                    const data = response.data;
                    setWinningProduct(data.winning_concept);
                    
                    // Mark data as loaded and update the previous runId
                    dataLoaded.current.summary = true;
                    prevRunId.current = runId;
                } catch (error) {
                    handleAPIError(error);
                }
            };

            fetchAPIData();
        }
    }, [projectId, runId, handleAPIError]);

    useEffect(() => {
        if (prevProjectId.current !== projectId) {
            dataLoaded.current.sidebar = false;
            dataLoaded.current.summary = false;
        } else if (prevRunId.current !== runId) {
            dataLoaded.current.summary = false;
        }
    }, [projectId, runId]);

    // Close modals when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            const stimuliModal = document.querySelector('.stimuli-modal');
            const personaModal = document.querySelector('.persona-modal');
            
            if (stimuliModal && !stimuliModal.contains(event.target) && 
                !event.target.closest('.stimuli-item')) {
                setClickedStimuli(prev => ({...prev, visible: false}));
            }
            
            if (personaModal && !personaModal.contains(event.target) && 
                !event.target.closest('.persona-item')) {
                setClickedPersona(prev => ({...prev, visible: false}));
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);



    return (
        <>
            <div className={`flex h-screen overflow-hidden ${(clickedStimuli.visible || clickedPersona.visible) ? 'blur-sm' : ''}`}>
                <div className=" bg-white border-r border-[#E1E1E1] flex flex-col sticky left-0 top-0 h-screen ">
                    {/* Header */}
                    <div className="sticky top-0 z-10 bg-white flex flex-col border-b w-[300px]">
                        <div className='flex justify-between items-center py-2 mt-5 px-2'>
                            <Link
                                to={"/"}
                                className=" block ml-3"
                                key="logo-link"
                            >
                                <img
                                    src={logo}
                                    alt="Logo of the Lens app"
                                    className="h-8 w-auto object-contain"
                                    loading='lazy'
                                    key="logo-img"
                                />
                            </Link>
                            <div className='bg-[#F8F9FA] flex items-center gap-2  px-[10px] rounded-[40px]  cursor-pointer h-[28px]' onClick={handleProjectSetupClick}>
                                <SettingIcon />
                                <span className='text-xs font-medium text-[#686A77]'>Project Setup</span>
                            </div>
                        </div>

                        {/* Main Navigation */}
                        <nav className="flex-grow py-5">
                            <ul className="space-y-1 p-1">
                                {/* Overview */}
                                <li key="overview">
                                    <NavLink
                                        to={`/${projectId}/${runId}/concept-report`}
                                        className={({ isActive }) =>
                                            `flex items-center px-5 py-[10px] gap-2 rounded ${isActive
                                                ? 'bg-[#F5F6FF] text-[#4F46E5] font-medium'
                                                : 'font-normal text-[#323942]'
                                            }`
                                        }
                                    >
                                        {({ isActive }) => (
                                            <>
                                                {isActive ? <OverviewFilledIcon /> : <OverviewIcon />}
                                                <span className='text-base'>Overview</span>
                                            </>
                                        )}
                                    </NavLink>
                                </li>

                                {/* Analysis with Dropdown */}
                                <li key="analysis">
                                    <div
                                        onClick={toggleAnalysis}
                                        className="flex items-center justify-between px-5 py-[10px] rounded cursor-pointer"
                                    >
                                        <div className="flex items-center gap-2">
                                            {isAnalysisOpen ? (<AnalysisFilledIcon />) : (<AnalysisIcon />)}
                                            <span className='text-[#323942] font-normal text-base'>Analysis</span>
                                        </div>
                                        {isAnalysisOpen ? (<FiChevronDown className='w-4 h-4 text-[#323942]' />) : (<FiChevronRight className='w-4 h-4 text-[#323942]' />)}
                                    </div>

                                    {/* ============================================
                                        FEATURE FLAGS - Conditional Navigation
                                        @author Sabya | @date September 30, 2025

                                        Each navigation item is conditionally rendered
                                        based on the feature flag from Redux store.
                                        Hidden features won't appear in the sidebar.
                                        ============================================ */}
                                    {isAnalysisOpen && (
                                        <ul className=" space-y-1 mt-1">
                                            {/* Purchase Drivers - conditionally rendered */}
                                            {features.purchaseDrivers && (
                                                <li key="purchase-drivers">
                                                    <NavLink to={`/${projectId}/${runId}/purchase-drivers`} className={({ isActive }) => `flex items-center px-[60px] py-[10px] gap-2 rounded ${isActive
                                                        ? 'bg-[#F5F6FF] text-[#4F46E5] font-normal'
                                                        : 'font-normal text-[#323942]'
                                                    }`}>
                                                        <span className=' text-base'>Purchase Drivers</span>
                                                    </NavLink>
                                                </li>
                                            )}
                                            {/* Concept Analysis - conditionally rendered */}
                                            {features.conceptAnalysis && (
                                                <li key="concept-analysis">
                                                    <NavLink to={`/${projectId}/${runId}/concept-analysis`} className={({ isActive }) => `flex items-center px-[60px] py-[10px] gap-2 rounded ${isActive
                                                        ? 'bg-[#F5F6FF] text-[#4F46E5] font-normal'
                                                        : 'font-normal text-[#323942]'
                                                    }`}>
                                                        <span className=' text-base'>Concepts</span>
                                                    </NavLink>
                                                </li>
                                            )}
                                            {/* Success Factors - conditionally rendered */}
                                            {features.successFactors && (
                                                <li key="success-factor">
                                                    <NavLink to={`/${projectId}/${runId}/success-factor`} className={({ isActive }) => `flex items-center px-[60px] py-[10px] gap-2 rounded ${isActive
                                                        ? 'bg-[#F5F6FF] text-[#4F46E5] font-normal'
                                                        : 'font-normal text-[#323942]'
                                                    }`}>
                                                        <span className=' text-base'>Success Factors</span>
                                                    </NavLink>
                                                </li>
                                            )}
                                            {/* Persona Analysis - conditionally rendered */}
                                            {features.personaAnalysis && (
                                                <li key="persona-analysis">
                                                    <NavLink to={`/${projectId}/${runId}/persona-analysis`} className={({ isActive }) => `flex items-center px-[60px] py-[10px] gap-2 rounded ${isActive
                                                        ? 'bg-[#F5F6FF] text-[#4F46E5] font-normal'
                                                        : 'font-normal text-[#323942]'
                                                    }`}>
                                                        <span className=' text-base'>Personas</span>
                                                    </NavLink>
                                                </li>
                                            )}
                                            {/* Comparing Concepts - conditionally rendered */}
                                            {features.comparingConcepts && (
                                                <li key="compare-concept">
                                                    <NavLink to={`/${projectId}/${runId}/compare-concept`} className={({ isActive }) => `flex items-center px-[60px] py-[10px] gap-2 rounded ${isActive
                                                        ? 'bg-[#F5F6FF] text-[#4F46E5] font-normal'
                                                        : 'font-normal text-[#323942]'
                                                    }`}>
                                                        <span className=' text-base'>{projectType === 'existing_product'? 'Comparing Concepts to the benchmark':'Comparing Concepts to each other'}</span>
                                                    </NavLink>
                                                </li>
                                            )}
                                        </ul>
                                    )}
                                </li>

                                {/* ============================================
                                    FEATURE FLAGS - Scores (Top-level)
                                    @author Sabya | @date September 30, 2025
                                    ============================================ */}
                                {features.scores && (
                                    <li key="scores">
                                        <NavLink
                                            to={`/${projectId}/${runId}/scores`}
                                            className={({ isActive }) =>
                                                `flex items-center px-5 py-[10px] gap-2 rounded ${isActive
                                                    ? 'bg-[#F5F6FF] text-[#4F46E5] font-normal'
                                                    : 'font-normal text-[#323942]'
                                                }`
                                            }
                                        >
                                            {({ isActive }) => (
                                                <>
                                                    {isActive ? <ScoresFilledIcon /> : <ScoresIcon />}
                                                    <span className='text-base'>Scores</span>
                                                </>
                                            )}
                                        </NavLink>
                                    </li>
                                )}

                                {/* ============================================
                                    FEATURE FLAGS - Radar (Top-level)
                                    @author Sabya | @date September 30, 2025
                                    ============================================ */}
                                {features.radar && (
                                    <li key="radar">
                                        <NavLink
                                            to={`/${projectId}/${runId}/radar`}
                                            className={({ isActive }) =>
                                                `flex items-center px-5 py-[10px] gap-2 rounded ${isActive
                                                    ? 'bg-[#F5F6FF] text-[#4F46E5] font-normal'
                                                    : 'font-normal text-[#323942]'
                                                }`
                                            }
                                        >
                                            {({ isActive }) => (
                                                <>
                                                    {isActive ? <RadarFilledIcon /> : <RadarIcon />}
                                                    <span className='text-base'>Radar</span>
                                                </>
                                            )}
                                        </NavLink>
                                    </li>
                                )}

                                {/* ============================================
                                    FEATURE FLAGS - Ideal Concept (Top-level)
                                    @author Sabya | @date September 30, 2025
                                    ============================================ */}
                                {features.idealConcept && (
                                    <li key="ideal-concept">
                                        <NavLink
                                            to={`/${projectId}/${runId}/ideal-concept`}
                                            className={({ isActive }) =>
                                                `flex items-center px-5 py-[10px] gap-2 rounded ${isActive
                                                    ? 'bg-[#F5F6FF] text-[#4F46E5] font-normal'
                                                    : 'font-normal text-[#323942]'
                                                }`
                                            }
                                        >
                                            {({ isActive }) => (
                                                <>
                                                    {isActive ? <DiamondFilledIcon /> : <DiamondIcon />}
                                                    <span className='text-base'>Ideal Concept</span>
                                                </>
                                            )}
                                        </NavLink>
                                    </li>
                                )}
                            </ul>
                        </nav>
                    </div>

                    {/* Section */}
                    <div className="px-4 py-4 space-y-3 flex-1 overflow-y-auto w-[300px]">
                        <div className='space-y-3'>
                            <div className='flex justify-between items-center'>
                                <div className='flex items-center gap-1 cursor-pointer' onClick={() => setIsConceptOpen(!isConceptOpen)}>
                                    <span className='font-medium text-xs text-[#686A77]'>CONCEPTS TESTED</span>
                                    {isConceptOpen ? <FiChevronUp color='#686A77' /> : <FiChevronDown color='#686A77' />}
                                </div>
                                <button 
                                    className='px-3 py-1 rounded-[100px] text-[#4F46E5] text-xs font-medium flex items-center gap-1'
                                    style={{
                                        background: 'var(--Accent-primary-100, #F5F6FF)',
                                        fontFamily: 'Manrope',
                                        fontWeight: 500,
                                        fontSize: '12px',
                                        lineHeight: '100%',
                                        letterSpacing: '0%'
                                    }}
                                    onClick={() => navigate(`/${projectId}/concept`, { state: { isExistingProject: true } })}
                                >
                                    <FiPlus className='w-3 h-3' />
                                    Add New
                                </button>
                            </div>

                            {isConceptOpen ? (
                                <div className='flex flex-col space-y-[10px]'>
                                    {concepts ? (concepts.map((concept, index) => {
                                        // Fixed: Extract image URL from nested API structure - September 23, 2025 - Sabya
                                        // Updated to handle concept.concept_image_infos[0].public_url and fallback paths
                                        const imageUrl = concept.concept_image_infos?.[0]?.public_url || 
                                                        concept.elements_image_infos?.[0]?.public_url || 
                                                        concept.image_url || 
                                                        concept.imageUrl || 
                                                        concept.stimulus_url || 
                                                        concept.stimulusUrl || 
                                                        conceptImages[index % conceptImages.length];
                                        
                                        return (
                                        <div
                                            key={index}
                                            className='flex bg-white border border-[#F5F6FF] rounded-lg p-2 gap-2 cursor-pointer h-fit stimuli-item'
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setClickedStimuli({
                                                    visible: !clickedStimuli.visible || 
                                                                    clickedStimuli.index !== index,
                                                    data: concept,
                                                    index: index
                                                });
                                            }}
                                        >
                                            <img
                                                src={imageUrl}
                                                alt={`product_${index}`}
                                                className='min-h-[44px] min-w-[44px] object-contain'
                                                loading='lazy'
                                                onError={(e) => {
                                                    // Fallback to static images if API image fails to load
                                                    e.target.src = conceptImages[index % conceptImages.length];
                                                }}
                                            />

                                            <div className='flex flex-col justify-center'>

                                                {concept.is_benchmark ? <span className='font-manrope font-bold text-[10px] leading-[16px] tracking-[0.1em] uppercase text-[#006638]'>Benchmark</span> : null}

                                                <div className='flex gap-2 items-center'>
                                                    <span className='text-[#060606] font-semibold text-base'>{concept.concept_name}</span>
                                                </div>
                                                <span className='text-[#060606] font-light text-xs line-clamp-2 overflow-hidden text-ellipsis'>{concept.title}</span>
                                            </div>
                                        </div>
                                        );
                                    })) : (
                                        <div className="h-8 w-8 border-[4px] border-[#4F46E5] border-t-transparent rounded-full animate-spin"></div>
                                    )}
                                </div>
                            ) : null}
                        </div>

                        <div className='space-y-3'>
                            <div className='flex justify-between items-center'>
                                <div className='flex items-center gap-1 cursor-pointer' onClick={() => setIsPersonasOpen(!isPersonasOpen)}>
                                    <span className='font-medium text-xs text-[#686A77]'>PERSONAS</span>
                                    {isPersonasOpen ? <FiChevronUp color='#686A77' /> : <FiChevronDown color='#686A77' />}
                                </div>
                                <button 
                                    className='px-3 py-1 rounded-[100px] text-[#4F46E5] text-xs font-medium flex items-center gap-1'
                                    style={{
                                        background: 'var(--Accent-primary-100, #F5F6FF)',
                                        fontFamily: 'Manrope',
                                        fontWeight: 500,
                                        fontSize: '12px',
                                        lineHeight: '100%',
                                        letterSpacing: '0%'
                                    }}
                                    onClick={() => navigate(`/${projectId}/personas`, { state: { isExistingProject: true } })}
                                >
                                    <FiPlus className='w-3 h-3' />
                                    Add New
                                </button>
                            </div>

                            {isPersonasOpen ? (
                                <div className='flex flex-col space-y-[10px]'>
                                    {personas ? (personas.map((item) => (
                                        <div className='flex items-center bg-white border border-[#F5F6FF] rounded-lg p-2 py-4 gap-[10px] cursor-pointer h-[80px] persona-item'
                                            key={item.persona_id}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setClickedPersona({
                                                    visible: !clickedPersona.visible,
                                                    persona: item,
                                                });
                                            }}
                                        >
                                            <div className='w-[34px] h-[34px] flex-shrink-0'>
                                                <img
                                                    src={item.avatar_url || MalePersonaImage}
                                                    alt="Persona"
                                                    className='rounded-full object-contain'
                                                    loading='lazy'
                                                    height={34}
                                                    width={34}
                                                />
                                            </div>

                                            <div className='flex flex-col'>
                                                <span className='text-[#060606] font-semibold text-base'>{item.persona_name}</span>
                                                <span className='font-light text-xs text-[#060606]'>{item.consumer_segment_name}</span>
                                            </div>
                                        </div>
                                    ))) : (<div className="h-8 w-8 border-[4px] border-[#4F46E5] border-t-transparent rounded-full animate-spin"></div>)}
                                </div>
                            ) : null}
                        </div>

                        <div className='space-y-3'>
                            <div className='flex justify-between items-center'>
                                <div className='flex items-center gap-1 cursor-pointer' onClick={() => setIsCompetitorsOpen(!isCompetitorsOpen)}>
                                    <span className='font-medium text-xs text-[#686A77]'>COMPETITORS</span>
                                    {isCompetitorsOpen ? <FiChevronUp color='#686A77' /> : <FiChevronDown color='#686A77' />}
                                </div>
                                <button 
                                    className='px-3 py-1 rounded-[100px] text-[#4F46E5] text-xs font-medium flex items-center gap-1'
                                    style={{
                                        background: 'var(--Accent-primary-100, #F5F6FF)',
                                        fontFamily: 'Manrope',
                                        fontWeight: 500,
                                        fontSize: '12px',
                                        lineHeight: '100%',
                                        letterSpacing: '0%'
                                    }}
                                    onClick={() => navigate(`/${projectId}/add-details`, { state: { isExistingProject: true } })}
                                >
                                    <FiPlus className='w-3 h-3' />
                                    Add New
                                </button>
                            </div>

                            {isCompetitorsOpen ? (
                                <div className='flex flex-col space-y-[10px]'>

                                    {competitors ? (
                                        Object.entries(competitors).map(([key, item]) => (
                                            <div key={key} className='flex items-center bg-white border border-[#F5F6FF] rounded-lg p-2 py-4 gap-[10px] h-[80px]'>
                                                <div className='w-[34px] h-[34px] flex-shrink-0'>
                                                    {item.image_url ? (
                                                        <img src={item.image_url} alt="Competitor" className='w-full h-full rounded-full object-contain' loading='lazy'/>
                                                    ) : (
                                                        <div className='w-full h-full rounded-full bg-[#F5F6FF] flex items-center justify-center'>
                                                            <span className='text-[#686A77] text-xs font-medium'>{item.title?.charAt(0)?.toUpperCase() || 'C'}</span>
                                                        </div>
                                                    )}
                                                </div>
                                                <div className='flex flex-col min-w-0'>
                                                    <span className='text-[#060606] font-semibold text-base truncate'>{item.title}</span>
                                                    <span className='font-light text-xs text-[#060606] truncate'>{key}</span>
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="h-8 w-8 border-[4px] border-[#4F46E5] border-t-transparent rounded-full animate-spin"></div>
                                    )}
                                </div>
                            ) : null}
                        </div>
                    </div>

                </div>
                <Suspense fallback={
                    <div className='w-full h-full flex items-center justify-center'>
                        <div className="h-12 w-12 border-4 border-[#4F46E5] border-t-transparent rounded-full animate-spin"></div>
                    </div>
                }>
                    <Outlet />
                </Suspense>
            </div>
            {/* Modal Backdrop */}
            {(clickedStimuli.visible || clickedPersona.visible) && (
                <div 
                    className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300"
                    onClick={(e) => {
                        e.stopPropagation();
                        if (clickedStimuli.visible) setClickedStimuli(prev => ({...prev, visible: false}));
                        if (clickedPersona.visible) setClickedPersona(prev => ({...prev, visible: false}));
                    }}
                ></div>
            )}
            
            {/* Concept Modal */}
            {clickedStimuli.visible && (
                <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
                    <div className="stimuli-modal w-full max-w-5xl h-[70vh] max-h-[700px] bg-white rounded-xl shadow-2xl overflow-hidden transform transition-all duration-300 ease-in-out">
                        <div className="relative h-full flex flex-col">
                            <button 
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setClickedStimuli(prev => ({...prev, visible: false}));
                                }}
                                className="absolute top-4 right-4 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-white bg-opacity-80 text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors duration-200"
                            >
                                ✕
                            </button>
                            <div className="overflow-y-auto flex-1 p-6">
                                <ConceptModal
                                    concept={clickedStimuli.data}
                                    isOpen={true}
                                    index={clickedStimuli.index}               
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Persona Modal */}
            {clickedPersona.visible && (
                <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
                    <div className="persona-modal w-full max-w-4xl h-[70vh] max-h-[700px] bg-white rounded-xl shadow-2xl overflow-hidden transform transition-all duration-300 ease-in-out">
                        <div className="relative h-full flex flex-col">
                            <button 
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setClickedPersona(prev => ({...prev, visible: false}));
                                }}
                                className="absolute top-4 right-4 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-white bg-opacity-80 text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors duration-200"
                            >
                                ✕
                            </button>
                            <div className="overflow-y-auto flex-1 p-6">
                                <ResultPersonasModal
                                    clickedPersona={clickedPersona.persona}
                                    isOpen={true}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}

export default ResultLayout